/**
 * <DatePicker> — date field + calendar sheet (molecule, L0.1).
 *
 * Un champ pressable (l'icône calendrier + la date écrite) qui ouvre la feuille du design system
 * (`<Modal>`) et son calendrier (`<MonthCalendar>`) : toucher un jour le choisit et referme. Le
 * calendrier Material de `react-native-paper-dates` (en-tête bleu de l'ancien thème) n'est plus
 * ouvert nulle part (Wajih, 03/11). L'API publique reste `value` / `onChange` en chaînes
 * `YYYY-MM-DD`.
 *
 * `withTime` enchaîne l'heure derrière le jour, dans la même feuille (« À quelle heure ? » : les
 * heures, puis les minutes de cinq en cinq, en pilules, et « Confirmer ») — une date d'heure en extra
 * ou le choix d'une occurrence se donnent en un seul champ. La valeur devient `YYYY-MM-DD HH:mm` ; sans la prop,
 * rien ne bouge, ni l'affichage ni le format. C'est une prop, pas une copie du composant.
 *
 * `maxDate` ferme la fenêtre par le haut et `placeholder` donne au champ un mot tant qu'il est
 * vide — une ligne de date à remplir ne peut pas écrire une date qu'elle n'a pas. Les deux ont un
 * défaut qui ne change rien pour les appelants d'aujourd'hui.
 *
 * L'heure de `withTime` s'écrit « 18h00 » : la branche n'a jamais eu d'appelant hors de l'espace
 * parent, où la marque écrit l'heure comme on la dit. Le champ sans heure ne bouge pas.
 *
 * No hex: field chrome uses theme tokens. `moment` formatting goes through `dateAdapter`.
 */
import React, { useState } from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { formatHour, formatLongDate, toDateOnly } from "../../../ports/primary/ui/formatters/dateFormatter"
import { Button } from "../../atoms/Buttons/Button"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Icon } from "../../atoms/icons/Icon"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { Modal } from "../Modal"
import { MonthCalendar } from "../MonthCalendar"

export type DatePickerProps = {
    /** Selected date as `YYYY-MM-DD`, or `YYYY-MM-DD HH:mm` when `withTime`. */
    value: string
    onChange: (next: string) => void
    /** Label above the field — e.g. "C'est pour quand ?". */
    label?: string
    /** Calendar modal title. Defaults to `label` or "Date". */
    modalLabel?: string
    /** Disallow dates before this (inclusive). Defaults to today (legacy assessment form). */
    minDate?: Date
    /** Dernière date choisissable. Absente → pas de borne haute, comme avant. */
    maxDate?: Date
    /**
     * Ce que le champ affiche tant que `value` est vide — « Choisir une date ». Absent, le champ
     * suppose une valeur et l'écrit : c'est le comportement des appelants d'aujourd'hui.
     */
    placeholder?: string
    /** Demander aussi l'heure, juste après le jour. La valeur passe à `YYYY-MM-DD HH:mm`. */
    withTime?: boolean
    /**
     * Le jour (`YYYY-MM-DD`) sur lequel le calendrier s'OUVRE, quand ce n'est pas la valeur : une
     * valeur passée, hors de `minDate`, ouvrirait sur un mois où aucun jour ne se choisit (« Ma dispo »,
     * `startingDatePickerValue`). Le champ écrit toujours la valeur. Absent (défaut) : la valeur.
     */
    openOn?: string
    disabled?: boolean
    testID?: string
}

/** Longueur de la partie « jour » d'une valeur, avec ou sans heure. */
const DATE_LENGTH = 10

/** `HH:mm` porté par la valeur, ou `null` quand elle n'a pas d'heure. */
function timeOf(value: string): { hours: number; minutes: number } | null {
    const match = /^\d{4}-\d{2}-\d{2}[ T](\d{2}):(\d{2})/.exec(value)
    return match ? { hours: Number(match[1]), minutes: Number(match[2]) } : null
}

const pad = (n: number): string => String(n).padStart(2, "0")

/** L'heure proposée quand la valeur n'en a pas encore : un créneau de fin de journée. */
const DEFAULT_TIME = { hours: 18, minutes: 0 }

const TEXTS = {
    dateTitle: "Date",
    timeTitle: "À quelle heure ?",
    hour: "Heure",
    minutes: "Minutes",
    confirm: "Confirmer",
}

export function DatePicker(props: DatePickerProps) {
    const {
        value,
        onChange,
        label,
        modalLabel,
        minDate = new Date(),
        maxDate,
        placeholder,
        withTime = false,
        openOn,
        disabled = false,
        testID,
    } = props

    const [step, setStep] = useState<"none" | "date" | "time">("none")
    // Le jour choisi attend son heure : sans ça, confirmer l'heure réécrirait l'ancienne date.
    const [pendingDate, setPendingDate] = useState<string | null>(null)

    const dayPart = value.slice(0, DATE_LENGTH)
    const time = timeOf(value)

    const empty = dayPart === ""
    const fieldLabel = empty
        ? (placeholder ?? "")
        : withTime
          ? `${formatLongDate(dayPart)}${time ? ` à ${formatHour(value)}` : ""}`
          : formatLongDate(value)

    const closeAll = () => {
        setStep("none")
        setPendingDate(null)
    }
    const minDay = toDateOnly(minDate)
    const maxDay = maxDate == null ? null : toDateOnly(maxDate)
    const chosenDay = pendingDate ?? dayPart
    // L'heure proposée : celle de la valeur, reprise à chaque ouverture du champ.
    const [pickedTime, setPickedTime] = useState<TimeOfDay>(DEFAULT_TIME)

    return (
        <Stack gap="3" testID={testID}>
            {label != null && label !== "" && (
                <Text size="text-base" weight="bold" color="primary">
                    {label}
                </Text>
            )}

            <Pressable
                disabled={disabled}
                onPress={() => {
                    setPickedTime(time ?? DEFAULT_TIME)
                    setStep("date")
                }}
                accessibilityRole="button"
                accessibilityLabel={label ?? "Choisir une date"}
                style={({ pressed }) => [styles.field(disabled), pressed && !disabled ? styles.pressed : undefined]}
            >
                <Row gap="3" align="center">
                    <View style={styles.iconWell}>
                        <Icon name="calendar-alt" size={16} color="role-strong" />
                    </View>
                    <Text
                        size="text-md"
                        weight="semibold"
                        color={empty ? "text-muted" : "text-strong"}
                        style={styles.flex}
                    >
                        {fieldLabel}
                    </Text>
                </Row>
            </Pressable>

            <Modal
                visible={step !== "none"}
                onClose={closeAll}
                title={step === "time" ? TEXTS.timeTitle : (modalLabel ?? label ?? TEXTS.dateTitle)}
                sizeToContent
                // Une feuille en étapes : le titre passe à la ligne au lieu d'être coupé, la croix reste
                // à droite, le retour au calendrier à gauche à l'étape de l'heure.
                steps={{
                    step: step === "time" ? 2 : 1,
                    total: withTime ? 2 : 1,
                    onBack: step === "time" ? () => setStep("date") : undefined,
                }}
                footer={
                    step === "time" ? (
                        <Button
                            label={TEXTS.confirm}
                            fullWidth
                            onPress={() => {
                                closeAll()
                                onChange(`${chosenDay} ${pad(pickedTime.hours)}:${pad(pickedTime.minutes)}`)
                            }}
                        />
                    ) : undefined
                }
            >
                {step === "time" ? (
                    <TimeOfDayChoice
                        dayLabel={chosenDay === "" ? "" : formatLongDate(chosenDay)}
                        value={pickedTime}
                        onChange={setPickedTime}
                    />
                ) : (
                    <MonthCalendar
                        value={chosenDay}
                        openOn={pendingDate ?? openOn}
                        minDay={minDay}
                        maxDay={maxDay}
                        onSelect={(picked) => {
                            if (!withTime) {
                                closeAll()
                                onChange(picked)
                                return
                            }
                            setPendingDate(picked)
                            setStep("time")
                        }}
                    />
                )}
            </Modal>
        </Stack>
    )
}

/** Les heures et les minutes qu'on touche : toutes les heures, les minutes de cinq en cinq. */
const HOURS = Array.from({ length: 24 }, (_, hour) => hour)
const MINUTES = Array.from({ length: 12 }, (_, index) => index * 5)

type TimeOfDay = { hours: number; minutes: number }

/** Une rangée de valeurs sous son intitulé, en puces. */
function ChipGroup(props: {
    title: string
    values: number[]
    label: (n: number) => string
    selected: number
    onPick: (n: number) => void
}) {
    const { title, values, label, selected, onPick } = props
    return (
        <Stack gap="2">
            <SectionLabel>{title}</SectionLabel>
            <Row gap="2" style={styles.wrap}>
                {values.map((n) => (
                    <ChoiceChip key={n} label={label(n)} selected={selected === n} onPress={() => onPick(n)} />
                ))}
            </Row>
        </Stack>
    )
}

/** L'heure d'un jour, en pilules : l'heure, puis les minutes. Une minute hors du pas reste proposée. */
function TimeOfDayChoice(props: { dayLabel: string; value: TimeOfDay; onChange: (next: TimeOfDay) => void }) {
    const { dayLabel, value, onChange } = props
    const minutes = MINUTES.includes(value.minutes) ? MINUTES : [...MINUTES, value.minutes].sort((a, b) => a - b)
    return (
        <Stack gap="4">
            {dayLabel !== "" && (
                <Text size="text-md" color="text-muted">
                    {dayLabel}
                </Text>
            )}
            <ChipGroup
                title={TEXTS.hour}
                values={HOURS}
                label={(hour) => `${hour}h`}
                selected={value.hours}
                onPick={(hours) => onChange({ ...value, hours })}
            />
            <ChipGroup
                title={TEXTS.minutes}
                values={minutes}
                label={pad}
                selected={value.minutes}
                onPick={(minutes) => onChange({ ...value, minutes })}
            />
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    field: (disabled: boolean) => ({
        flexDirection: "row" as const,
        alignItems: "center" as const,
        padding: theme.spacing["4"],
        borderRadius: theme.radius.lg,
        borderWidth: 2,
        borderColor: theme.colors.border,
        backgroundColor: theme.colors["surface-raised"],
        opacity: disabled ? 0.5 : 1,
    }),
    pressed: { opacity: 0.85 },
    iconWell: {
        width: 36,
        height: 36,
        borderRadius: theme.radius.full,
        backgroundColor: theme.colors["primary-soft"],
        alignItems: "center" as const,
        justifyContent: "center" as const,
    },
    flex: { flexShrink: 1 },
    wrap: { flexWrap: "wrap" },
}))
